import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useScreenshotMode } from "@/contexts/ScreenshotModeContext";
import { Button } from "@/components/ui/button";
import { Camera, CameraOff } from "lucide-react";

interface ScreenshotModeToggleProps {
  variant?: "floating" | "inline";
}

/**
 * Admin-only floating control that hides marketing-unfriendly UI chrome
 * (test-mode banner, beta badge, demo controls, env indicators) for clean
 * screenshots and videos. State persists in localStorage.
 */
const ScreenshotModeToggle = ({ variant = "floating" }: ScreenshotModeToggleProps = {}) => {
  const { isAdmin, isSuperAdmin } = useAuth();
  const { enabled, toggle } = useScreenshotMode();
  const location = useLocation();

  if (!isAdmin && !isSuperAdmin) return null;
  // Only render the trigger inside the admin area so it never leaks into
  // screenshots themselves. Once enabled it keeps working everywhere.
  if (!location.pathname.startsWith("/admin")) return null;

  const wrapperClass =
    variant === "inline"
      ? "w-full print:hidden"
      : "fixed bottom-16 right-4 z-[60] print:hidden";
  const btnClass =
    variant === "inline"
      ? (enabled
          ? "w-full justify-start bg-primary text-primary-foreground hover:bg-primary/90 gap-2"
          : "w-full justify-start bg-sidebar-accent text-sidebar-foreground hover:bg-sidebar-accent/80 gap-2")
      : (enabled
          ? "shadow-lg bg-primary text-primary-foreground hover:bg-primary/90 gap-2"
          : "shadow-lg bg-foreground/85 text-background hover:bg-foreground gap-2");

  return (
    <div className={wrapperClass}>
      <Button
        type="button"
        size="sm"
        onClick={toggle}
        className={btnClass}
        aria-label="Screenshot Mode"
        aria-pressed={enabled}
        title={enabled ? "Screenshot Mode is ON — chrome hidden" : "Screenshot Mode is OFF"}
      >
        {enabled ? <CameraOff className="h-4 w-4" /> : <Camera className="h-4 w-4" />}
        {enabled ? "Screenshot: ON" : "Screenshot Mode"}
      </Button>
    </div>
  );
};

export default ScreenshotModeToggle;